import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'

import { getOrganizationInfoAppOrganizationOrganizationsInfoGetOptions } from '@/client/@tanstack/react-query.gen'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useAuth } from '@/hooks/use-auth'
import { AccountsReviewedChart } from './-components/accounts-reviewed-chart'
import { ActivityLeaderboard } from './-components/activity-leaderboard'
import { ActivityLog } from './-components/activity-log'
import { ActivityStats } from './-components/activity-stats'
import { OutreachActivityChart } from './-components/outreach-activity-chart'
import { PlatformInsights } from './-components/platform-insights'
import { RenewalReportButton } from './-components/renewal-report-button'
import { ActivityPageSkeleton } from './-components/skeleton'

type TimePeriod = 7 | 30 | 90 | undefined
type DashboardTab = 'overview' | 'leaderboard' | 'log'

function ActivityDashboardPage() {
  const {
    isLoading: isAuthLoading,
    isOrgAdmin,
  } = useAuth()
  const [includeDistributors, setIncludeDistributors] = useState(true)
  const [timePeriod, setTimePeriod] = useState<TimePeriod>(30)
  const [activeTab, setActiveTab] = useState<DashboardTab>('overview')

  // Fetch org members for activity log user filter
  const { data: orgInfo, isLoading: isOrgInfoLoading } = useQuery(
    getOrganizationInfoAppOrganizationOrganizationsInfoGetOptions(),
  )

  if (isAuthLoading) {
    return <ActivityPageSkeleton />
  }

  return (
    <div className="flex flex-col gap-6 px-4 py-4 lg:px-6 md:py-6">
      {/* Supporting stats - compact */}
      <ActivityStats includeDistributors={includeDistributors} />

      {/* Main tabs */}
      <Tabs value={activeTab} onValueChange={v => setActiveTab(v as DashboardTab)}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <TabsList className="max-w-full overflow-x-auto">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="leaderboard">Leaderboard</TabsTrigger>
            <TabsTrigger value="log">
              <span className="sm:hidden">Log</span>
              <span className="hidden sm:inline">Activity Log</span>
            </TabsTrigger>
          </TabsList>

          {/* Controls - only show for overview and leaderboard tabs */}
          {activeTab !== 'log' && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-4">
              <Tabs
                value={timePeriod === undefined ? 'all' : String(timePeriod)}
                onValueChange={(v) => {
                  setTimePeriod(v === 'all' ? undefined : (Number(v) as 7 | 30 | 90))
                }}
              >
                <TabsList>
                  <TabsTrigger value="7">
                    <span className="sm:hidden">7d</span>
                    <span className="hidden sm:inline">7 Days</span>
                  </TabsTrigger>
                  <TabsTrigger value="30">
                    <span className="sm:hidden">30d</span>
                    <span className="hidden sm:inline">30 Days</span>
                  </TabsTrigger>
                  <TabsTrigger value="90">
                    <span className="sm:hidden">90d</span>
                    <span className="hidden sm:inline">90 Days</span>
                  </TabsTrigger>
                  <TabsTrigger value="all">
                    <span className="sm:hidden">All</span>
                    <span className="hidden sm:inline">All Time</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              <div className="flex items-center gap-2">
                <Switch
                  id="include-distributors"
                  checked={includeDistributors}
                  onCheckedChange={setIncludeDistributors}
                />
                <Label htmlFor="include-distributors" className="text-sm">
                  Incl. distributors
                </Label>
              </div>
            </div>
          )}
        </div>

        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Platform insights - competitors and researching status */}
          <PlatformInsights />

          {/* Charts - side by side on larger screens */}
          <div className="grid gap-6 lg:grid-cols-2">
            <OutreachActivityChart includeDistributors={includeDistributors} days={timePeriod} />
            <AccountsReviewedChart days={timePeriod} />
          </div>
        </TabsContent>

        <TabsContent value="leaderboard" className="mt-6">
          <ActivityLeaderboard includeDistributors={includeDistributors} days={timePeriod} />
        </TabsContent>

        <TabsContent value="log" className="mt-6">
          <ActivityLog members={orgInfo?.members ?? []} isMembersLoading={isOrgInfoLoading} />
        </TabsContent>
      </Tabs>

      {/* Reports (admin-only). Gate the entire section, not just the button,
          so non-admins don't see a prompt they can't act on. */}
      {isOrgAdmin && (
        <section className="flex flex-col gap-3 rounded-md border bg-muted/20 p-4">
          <div>
            <h2 className="text-base font-semibold">Reports</h2>
            <p className="text-sm text-muted-foreground">
              Generate a report summarizing what SUPPLYCO delivered for your team. When it's ready we'll add it to your inbox, where you can email it to yourself or all org admins.
            </p>
          </div>
          <RenewalReportButton />
        </section>
      )}
    </div>
  )
}

export const Route = createFileRoute('/_authed/_user/activity/')({
  component: ActivityDashboardPage,
  pendingComponent: ActivityPageSkeleton,
  beforeLoad: () => ({
    getTitle: () => 'Activity Dashboard',
  }),
})
